import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  Spinner,
} from "@fluentui/react-components";
import { Client } from "@microsoft/microsoft-graph-client";
import {
  Cell,
  CellTemplate,
  Compatible,
  getCellProperty,
  Uncertain,
  UncertainCompatible,
} from "@silevis/reactgrid";
import { FluentPeoplePicker } from "@skanska/vsa-people-picker";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FieldTypes } from "../../models/FieldTypes";
import { UserInformation } from "../../models/UserInformation";
import { stopKeyDownPropagation } from "../../services/ReactGridService";
import { DisplayField } from "../DisplayField";

export interface UserCell extends Cell {
  type: "users";
  text: string;
  userValue: UserCellValue;
}

export interface UserCellValue {
  label: string;
  users: UserInformation[];
}

const useDialogStyles = makeStyles({
  dialogSurface: {
    minWidth: "200px",
  },
  peoplePickerContainer: {
    minHeight: "100px",
  },
});

interface UserPickerDialogProps {
  graphClient: Client | null;
  groupId: string;
  label: string;
  users: UserInformation[];
  showTagsAsDialogTrigger?: boolean;
  onSubmit: (users: UserInformation[]) => void;
  onClose?: () => void;
}

const UserPickerDialog = (props: UserPickerDialogProps) => {
  const classes = useDialogStyles();
  const { t } = useTranslation();
  const {
    graphClient,
    groupId,
    label,
    users,
    showTagsAsDialogTrigger,
    onSubmit,
    onClose,
  } = props;
  const selectedUsersRef = useRef<UserInformation[]>(users);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    selectedUsersRef.current = users;
  }, [users]);

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    if (document.activeElement?.className === "rg-hidden-element") {
      setOpen(true);
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, []);

  const getUserIds = (userList: UserInformation[]): string[] => {
    return userList.map((u) => u.id).filter(Boolean) as string[];
  };

  const onClickSave = () => {
    onSubmit(selectedUsersRef.current);
    setOpen(false);
  };

  const onClickClose = () => {
    if (onClose) {
      onClose();
    }
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, data) => {
        // Only allow closing via our explicit close/save buttons
        if (!data.open) {
          return;
        }
        setOpen(data.open);
      }}
    >
      <DialogTrigger disableButtonEnhancement>
        {showTagsAsDialogTrigger ? (
          <div
            role="button"
            style={{ cursor: "pointer" }}
            onCopy={(e) => e.stopPropagation()}
            onCut={(e) => e.stopPropagation()}
            onPaste={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setOpen(true)}
            onKeyDown={stopKeyDownPropagation}
          >
            <DisplayField
              type={FieldTypes.User}
              label={label}
              value={users}
              disableTextWrap
            />
          </div>
        ) : (
          <Button
            onCopy={(e) => e.stopPropagation()}
            onCut={(e) => e.stopPropagation()}
            onPaste={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setOpen(true)}
            onKeyDown={stopKeyDownPropagation}
          >
            {t("searchName")}
          </Button>
        )}
      </DialogTrigger>
      <DialogSurface
        onCopy={(e) => e.stopPropagation()}
        onCut={(e) => e.stopPropagation()}
        onPaste={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        className={classes.dialogSurface}
        aria-describedby={undefined}
      >
        <DialogBody>
          <DialogTitle>
            {t("select")} {label}
          </DialogTitle>
          <DialogContent>
            <div className={classes.peoplePickerContainer}>
              {graphClient ? (
                <FluentPeoplePicker
                  graphClient={graphClient}
                  groupId={groupId}
                  placeholder={t("searchName")}
                  multiSelect={false}
                  defaultSelectedUserIds={getUserIds(users)}
                  hideWhenSelected={true}
                  onChange={(newUsers: UserInformation[]) => {
                    selectedUsersRef.current = newUsers;
                  }}
                />
              ) : (
                <Spinner size="tiny" />
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClickClose} appearance="secondary">
              {t("cancel")}
            </Button>
            <Button onClick={onClickSave} appearance="primary">
              {t("save")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const UserCellTemplate = (
  graphClient: Client | null,
  groupId: string
): CellTemplate<UserCell> => {
  const [shouldOpenDialogOpen, setShouldOpenDialog] = useState<boolean>(false);

  const handleKeyDown = (
    cell: Compatible<UserCell>,
    _keyCode: number,
    _ctrl: boolean,
    _shift: boolean,
    _alt: boolean,
    key?: string
  ) => {
    const enableEdit = key === "Enter" || key === "DoubleClick";

    setShouldOpenDialog(enableEdit);

    return {
      cell: cell,
      enableEditMode: enableEdit,
    };
  };

  const getCompatibleCell = (
    uncertainCell: Uncertain<UserCell>
  ): Compatible<UserCell> => {
    const text = getCellProperty(uncertainCell, "text", "string");
    const userCellValue: UserCellValue = JSON.parse(text);

    if (!userCellValue.label) {
      throw Error("Failed to parse user cell");
    }

    return { ...uncertainCell, text, userValue: userCellValue, value: NaN };
  };

  const onPickerChange = (
    newUsers: UserInformation[],
    cell: Compatible<UserCell>,
    onCellChanged: (cell: Compatible<UserCell>, commit: boolean) => void
  ) => {
    const newUserValue: UserCellValue = {
      label: cell.userValue.label,
      users: newUsers,
    };
    const newTextValue = JSON.stringify(newUserValue);

    cell.userValue = newUserValue;
    cell.text = newTextValue;

    onCellChanged(cell, true);
  };

  const getClearedUserValueText = (oldUserValue: UserCellValue) => {
    const clearedValue: UserCellValue = { ...oldUserValue, users: [] };
    return JSON.stringify(clearedValue);
  };

  const onPickerClose = (
    cell: Compatible<UserCell>,
    onCellChanged: (cell: Compatible<UserCell>, commit: boolean) => void
  ) => {
    onCellChanged(cell, true);
    setShouldOpenDialog(false);
  };

  const update = (
    cell: Compatible<UserCell>,
    cellToMerge: UncertainCompatible<UserCell>
  ): Compatible<UserCell> => {
    const cellText = cellToMerge.text
      ? cellToMerge.text
      : getClearedUserValueText(cell.userValue);

    return getCompatibleCell({ ...cell, text: cellText });
  };

  const render = (
    cell: Compatible<UserCell>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<UserCell>, commit: boolean) => void
  ): React.ReactNode => {
    return isInEditMode && shouldOpenDialogOpen ? (
      <UserPickerDialog
        graphClient={graphClient}
        groupId={groupId}
        label={cell.userValue.label}
        users={cell.userValue.users}
        showTagsAsDialogTrigger
        onSubmit={(users) => onPickerChange(users, cell, onCellChanged)}
        onClose={() => onPickerClose(cell, onCellChanged)}
      />
    ) : (
      <DisplayField
        type={FieldTypes.User}
        label={cell.userValue.label}
        value={cell.userValue.users}
        disableTextWrap
      />
    );
  };

  return { getCompatibleCell, render, update, handleKeyDown };
};
